import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { observer } from "mobx-react";

type Actions = DialogPrimitive.Root.Actions;

export interface DiscardPromptProps {
  open: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

/** The "Discard changes?" prompt, injected from `root.tsx` (NG-2717).
 *
 *  ui/ may not import i18n or primitives/ (dependency-cruiser R-L1), so the translated
 *  prompt lives in `primitives/discard-changes-prompt.tsx` and reaches every dialog
 *  through this context. With no provider the guard is inert and a dialog behaves as it
 *  did before NG-2717 — which is what a bare `render()` in a test gets. */
const DiscardPromptContext = React.createContext<React.ComponentType<DiscardPromptProps> | null>(
  null,
);
export const DiscardPromptProvider = DiscardPromptContext.Provider;

const noop = () => {};

/** The nearest dialog's `markChanged`. Stable for the dialog's lifetime, and kept apart
 *  from the prompt slot, so the many controls that read it never re-render because of
 *  the guard. */
const MarkChangedContext = React.createContext<() => void>(noop);

/** The prompt element, rendered by `DialogContent` / `SheetContent` inside their own
 *  popup — which makes base-ui treat it as a nested dialog, so a click in it is not an
 *  outside press on the parent — and whether it is showing, so the popup can mark
 *  itself (`data-discard-prompt-open`) and step back behind it. */
interface PromptSlot {
  node: React.ReactNode;
  open: boolean;
}
const PromptSlotContext = React.createContext<PromptSlot>({ node: null, open: false });

/** Report that the person changed something in the nearest dialog or sheet.
 *
 *  Typed input is detected on its own (bubbling `input`/`change` events). **A control
 *  that changes a value on a click alone — a swatch, a row toggle, a cmdk item, a drag —
 *  fires neither, and must report itself or the guard cannot see it.** Marks on any user
 *  pick, an unchanged value included. A no-op outside a dialog, so a shared control can
 *  call it unconditionally. Must be called from a component rendered INSIDE the dialog:
 *  the component that renders `<Dialog>` sits above its scope (use `data-unsaved-change`
 *  there). */
export function useMarkUnsavedChange(): () => void {
  return React.useContext(MarkChangedContext);
}

/** `fn`, wrapped to report the change to the nearest dialog first — the one-liner for a
 *  click-only control's `onChange`. See `useMarkUnsavedChange`. */
export function useReportedChange<A extends unknown[]>(
  fn: ((...args: A) => void) | undefined,
): (...args: A) => void {
  const markChanged = useMarkUnsavedChange();
  return (...args: A) => {
    markChanged();
    fn?.(...args);
  };
}

/** Search and filter boxes change nothing worth keeping, and a region that saves on its
 *  own (a comment box) holds nothing for the dialog to protect. Typed input and drops
 *  inside a `data-unsaved-ignore` element don't count. Hook-reported picks (Select, cmdk,
 *  DatePicker) inside one still do. */
const IGNORED = 'input[type="search"], [data-slot="command-input"], [data-unsaved-ignore]';

/** A click on an element carrying this attribute counts as a change. For a one-off
 *  click-only control written inside the very component that renders the `<Dialog>`,
 *  where `useMarkUnsavedChange()` cannot reach the dialog's scope (it sits above it). */
const CLICK_CHANGES = "[data-unsaved-change]";

/** Which popup saw each native event first. React events bubble through portals, so an
 *  edit inside a nested dialog also reaches every ancestor popup; the innermost sees it
 *  first (bubble phase) and owns it, and the ancestors ignore it. */
const firstPopup = new WeakMap<Event, object>();

type HandlerKey = "onInput" | "onChange" | "onClick" | "onDrop";

/** The popup half of the guard, for `DialogContent` / `SheetContent`. Takes the popup's
 *  props and returns them with change detection composed into the caller's handlers:
 *   - `input` / `change`: typing, and base-ui Checkbox / Radio / Switch;
 *   - `drop`: a file or a dragged row landing inside the dialog;
 *   - `click` on a `data-unsaved-change` element. */
export function usePopupChangeListeners(props: DialogPrimitive.Popup.Props) {
  const markChanged = React.useContext(MarkChangedContext);
  const { node: prompt, open: promptOpen } = React.useContext(PromptSlotContext);
  const [token] = React.useState(() => ({}));

  /** The event's target if this popup owns the event, else null. */
  const ownTarget = (event: React.SyntheticEvent): Element | null => {
    if (!firstPopup.has(event.nativeEvent)) firstPopup.set(event.nativeEvent, token);
    if (firstPopup.get(event.nativeEvent) !== token) return null;
    return event.target instanceof Element ? event.target : null;
  };
  const changedUnlessIgnored = (event: React.SyntheticEvent) => {
    const target = ownTarget(event);
    if (target && !target.closest(IGNORED)) markChanged();
  };

  const handlers: Pick<DialogPrimitive.Popup.Props, HandlerKey> = {
    onInput: (e) => {
      changedUnlessIgnored(e);
      props.onInput?.(e);
    },
    onChange: (e) => {
      changedUnlessIgnored(e);
      props.onChange?.(e);
    },
    onDrop: (e) => {
      changedUnlessIgnored(e);
      props.onDrop?.(e);
    },
    onClick: (e) => {
      if (ownTarget(e)?.closest(CLICK_CHANGES)) markChanged();
      props.onClick?.(e);
    },
  };
  return {
    popupProps: {
      ...props,
      ...handlers,
      // Not base-ui's own `data-nested-dialog-open`, which marks a parent under ANY
      // nested dialog: the step-back is scoped to the discard prompt (NG-2717). To make
      // it general, drop this and the slot's `open` and key the style on that instead.
      "data-discard-prompt-open": promptOpen ? "" : undefined,
    },
    prompt,
  };
}

const GUARDED_REASONS: ReadonlySet<string> = new Set(["escape-key", "outside-press"]);

export type GuardedRootProps = DialogPrimitive.Root.Props & {
  dismissible?: boolean;
  unsavedChanges?: boolean;
};

/** The root shared by `Dialog` and `Sheet` (both base-ui Dialog roots). Close order,
 *  which is load-bearing:
 *   1. `dismissible={false}` refuses Esc / outside press.
 *   2. A recorded change (or `unsavedChanges`) turns Esc / outside press into the prompt.
 *   3. Everything else reaches the caller's `onOpenChange`.
 *  The guard runs BEFORE the caller because a controlled caller's `setOpen(false)` is
 *  already queued by the time a later `details.cancel()` could run.
 *
 *  A non-modal root (`modal={false}`) is unguarded unless `unsavedChanges` says
 *  otherwise: the page behind stays live, so every click on it is an outside press, and
 *  a prompt would fire on ordinary navigation. */
export const GuardedDialogRoot = observer(function GuardedDialogRoot({
  dismissible = true,
  unsavedChanges,
  onOpenChange,
  onOpenChangeComplete,
  actionsRef,
  ...props
}: GuardedRootProps) {
  const Prompt = React.useContext(DiscardPromptContext);
  const guarded = unsavedChanges ?? (props.modal === false ? false : undefined);
  const changedRef = React.useRef(false);
  // "unmounted" until first asked for, so the great majority of popups — which never
  // prompt — don't carry a closed nested Dialog.Root; kept mounted after that so the
  // prompt's exit transition still plays.
  const [promptState, setPromptState] = React.useState<"unmounted" | "open" | "closed">(
    "unmounted",
  );
  const ownActionsRef = React.useRef<Actions | null>(null);

  // Keep a caller-supplied actionsRef working: the guard needs base-ui's handle itself.
  React.useImperativeHandle(
    actionsRef,
    () => ({
      close: () => ownActionsRef.current?.close(),
      unmount: () => ownActionsRef.current?.unmount(),
    }),
    [],
  );

  const markChanged = React.useCallback(() => {
    changedRef.current = true;
  }, []);
  const keepEditing = React.useCallback(() => setPromptState("closed"), []);
  const discard = React.useCallback(() => {
    setPromptState("closed");
    // Reaches the caller's onOpenChange(false) with reason "imperative-action", so
    // controlled and uncontrolled dialogs close alike, and a caller that refuses
    // (mid-submit) still can.
    ownActionsRef.current?.close();
  }, []);

  const promptSlot = React.useMemo<PromptSlot>(
    () => ({
      node:
        Prompt && promptState !== "unmounted" ? (
          <Prompt open={promptState === "open"} onKeepEditing={keepEditing} onDiscard={discard} />
        ) : null,
      open: promptState === "open",
    }),
    [Prompt, promptState, keepEditing, discard],
  );

  return (
    <MarkChangedContext.Provider value={markChanged}>
      <PromptSlotContext.Provider value={promptSlot}>
        <DialogPrimitive.Root
          {...props}
          actionsRef={ownActionsRef}
          onOpenChange={(open, details) => {
            if (!open && GUARDED_REASONS.has(details.reason)) {
              if (!dismissible) {
                details.cancel();
                return;
              }
              if (Prompt && (guarded ?? changedRef.current)) {
                details.cancel();
                setPromptState("open");
                return;
              }
            }
            onOpenChange?.(open, details);
          }}
          onOpenChangeComplete={(open) => {
            // Reset on every finished close, however it happened — Save closes by
            // flipping `open`, which never reaches onOpenChange — so a reopen starts clean.
            if (!open) {
              changedRef.current = false;
              setPromptState("unmounted");
            }
            onOpenChangeComplete?.(open);
          }}
        />
      </PromptSlotContext.Provider>
    </MarkChangedContext.Provider>
  );
});
